"use client";

import { GoogleMap } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { MAP_DEFAULT_ZOOM, MAP_MAX_ZOOM, MAP_MIN_ZOOM, MAP_SEARCH_DELAY_SEC } from "@/lib/constants/map";
import Button from "../Button";
import { faCompressWide, faExpandWide, faList, faMinus, faPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";
import Markers from "./Markers";
import { Coordinates } from "@/lib/types/Map";
import { RoomProps } from "../Room";
import { SearchLocation } from "@/lib/utils/googleMapsUtils";
import { useLanguage } from "@/lib/hooks/useLanguage";
import { useGoogleMap } from "@/lib/hooks/useGoogleMap";
import { GoogleTagManagerEvents } from "@/lib/utils/googleTagEvents";
import { useAtomValue } from "jotai";
import { authAtom } from "@/lib/jotai/auth/authStore";

type Props = {
    coordinates: Coordinates | null;
    listings: RoomProps[];
    selectedRoom: RoomProps | null;
    selectedRoomMouseHover: RoomProps | null;
    onCloseMap?: () => void;
    onLocationChange: (location: SearchLocation) => void;
    onListingClick: (listing: RoomProps) => void;
    isMobile: boolean;
    isLoading?: boolean;
    isVisible?: boolean;
    onMapSet?: (map: google.maps.Map | null) => void;
};

const getLocationFromResult = (result: google.maps.GeocoderResult) => {
    const countryEntries = result.address_components.filter(
        (ac) => ac.types.includes("country") && ac.types.includes("political"),
    );
    let country = "";
    if (countryEntries.length > 0) {
        country = countryEntries[0].long_name;
    }
    const localityEntries = result.address_components.filter(
        (ac) => ac.types.includes("locality") && ac.types.includes("political"),
    );
    let city = "";
    if (localityEntries.length > 0) {
        city = localityEntries[0].long_name;
    }
    if (!city) {
        if (country === "United Kingdom") {
            const areaCityForUkEntries = result.address_components.filter((ac) => ac.types.includes("postal_town"));

            if (areaCityForUkEntries.length > 0) {
                return `${(city = areaCityForUkEntries[0].long_name)}, ${country}`;
            }
        }
        const area1Entries = result.address_components.filter(
            (ac) => ac.types.includes("administrative_area_level_1") && ac.types.includes("political"),
        );
        if (area1Entries.length > 0) {
            city = area1Entries[0].long_name;
        }
    }

    if (country && city) {
        return `${city}, ${country}`;
    }
    return null;
};

const SearchMap = ({
                       coordinates,
                       listings,
                       onCloseMap,
                       onLocationChange,
                       onListingClick,
                       isMobile,
                       selectedRoomMouseHover,
                       isLoading = false,
                       isVisible = true,
                       onMapSet,
                   }: Props) => {
    const lang = useLanguage();
    const isLoaded = useGoogleMap(lang);

    const refContainer = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [zoom, setZoom] = useState(MAP_DEFAULT_ZOOM);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [dragTimerActive, setDragTimerActive] = useState(false);
    const [currentCity, setCurrentCity] = useState("");
    const [prevCenter, setPrevCenter] = useState<Coordinates | null>(null);
    const { isLoggedIn, userInfo } = useAtomValue(authAtom);
    const [isCursorOverMap, setIsCursorOverMap] = useState(false);


    useEffect(() => {
        if (coordinates) {
            map?.panTo(coordinates);
        }
    }, [map, coordinates]);

    const onLoad = useCallback((map: google.maps.Map) => {
        console.log(`*** debug: onLoad`);
        setMap(map);
        if (onMapSet) onMapSet(map);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onUnmount = useCallback(() => {
        console.log(`*** debug: onUnmount`);
        setMap(null);
        if (onMapSet) onMapSet(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedRoomMouseHover) {
            console.log(`*** debug: is not selectedRoom: ${selectedRoomMouseHover}`);
            return;
        }
        console.log(`*** debug: is selectedRoom: ${selectedRoomMouseHover} moving to nw location`);
        map?.panTo({ lat: selectedRoomMouseHover.location.lat, lng: selectedRoomMouseHover.location.lng });
    }, [selectedRoomMouseHover]);

    // zoom
    const onZoomIn = useCallback(() => setZoom(Math.min(zoom + 1, MAP_MAX_ZOOM)), [zoom]);
    const onZoomOut = useCallback(() => setZoom(Math.max(zoom - 1, MAP_MIN_ZOOM)), [zoom]);
    const handleZoomChange = useCallback(() => {
        console.log(`*** debug: handleZoomChange isCursorOverMap: ${isCursorOverMap}`);
        setZoom(map?.getZoom() ?? MAP_DEFAULT_ZOOM);
    }, [map]);

    // fullscreen
    const handleChangeFullscreen = (isFullscreen: boolean) => {
        return (event: Event) => {
            if (event.target === refContainer.current) {
                console.log(`*** debug: Fullscreen changed before ${isFullscreen}`);
                setIsFullscreen(!isFullscreen);
                console.log(`*** debug: Fullscreen changed after ${isFullscreen}`);
            }
        };
    };
    useEffect(() => {
        const handleChange = handleChangeFullscreen(isFullscreen);
        console.log(`*** debug: handleChangeFullscreen`);
        document.addEventListener("fullscreenchange", handleChange);
        return () => document.removeEventListener("fullscreenchange", handleChange);
    }, [isFullscreen]);
    const onFullscreen = useCallback(() => {
        console.log(`*** debug: onFullscreen`);
        if (!isFullscreen) {
            console.log(`*** debug: onFullscreen if`);
            GoogleTagManagerEvents.clickOnMaximizeMapButton(lang, isLoggedIn ? userInfo._id : undefined);
            refContainer.current?.requestFullscreen();
        } else {
            console.log(`*** debug: onFullscreen else`);
            document.exitFullscreen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullscreen]);

    const search = useCallback(() => {
        console.log(`*** debug: search: ${dragTimerActive}`);
        if (!dragTimerActive) return;

        const currentCenter = map?.getCenter();
        if (!currentCenter) return;

        const currentLat = currentCenter.lat();
        const currentLng = currentCenter.lng();

        const hasMoved =
            !prevCenter || Math.abs(prevCenter.lat - currentLat) > 0.05 || Math.abs(prevCenter.lng - currentLng) > 0.05;
        console.log(`search: has moved: ${hasMoved} ${currentLat}, ${currentLng}`);
        if (!hasMoved) {
            setDragTimerActive(false);
            return;
        }

        setPrevCenter({ lat: currentLat, lng: currentLng });

        let timer: NodeJS.Timeout | null = null;

        timer = setTimeout(() => {
            if (isCursorOverMap) {
                const geocoder = new google.maps.Geocoder();
                geocoder
                    .geocode({ location: map?.getCenter() })
                    .then((response) => {
                        if (response.results[0]) {
                            const result = response.results[0];
                            const locationName = getLocationFromResult(result);
                            // Only call onLocationChange if the city has changed
                            if (locationName && locationName !== currentCity && isCursorOverMap) {
                                setCurrentCity(locationName);
                                onLocationChange({ name: locationName });
                            } else {
                                console.log(`*** debug: map: locationName: ${locationName} currentCity: ${currentCity} isCursorOverMap: ${isCursorOverMap}`);
                            }
                        }
                    })
                    .catch((e) => console.error("Geocoder failed due to: " + e));
            }else {
                console.log(`*** debug: map: currentCity: ${currentCity} isCursorOverMap: ${isCursorOverMap}`);
            }
        }, 2000 * MAP_SEARCH_DELAY_SEC);
        console.log(`*** debug: search: timer set`);
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [dragTimerActive, map, currentCity, onLocationChange]);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        console.log(`*** debug: dragTimerActive: ${dragTimerActive}`);
        if (dragTimerActive) {
            
            cleanup = search();
        }
        return cleanup;
    }, [dragTimerActive, search]);

    const onIdle = useCallback(() => {
        setDragTimerActive(true);
    }, []);

    const buttonSize = "h-12 w-12";
    return (
        <div className={`bg-gray-200 h-full w-full relative ${isVisible ? "" : "hidden"}`} ref={refContainer}
             onMouseEnter={() => {
                 console.log("Mouse entered the map container before: ", isCursorOverMap);
                 setIsCursorOverMap(true);
                 console.log("Mouse entered the map container after: ", isCursorOverMap);
             }}
             onMouseLeave={() => {
                 console.log("Mouse is no longer in the map container before: ", isCursorOverMap);
                 setIsCursorOverMap(false);
                 console.log("Mouse is no longer in the map container after: ", isCursorOverMap);
             }}>
            {isLoaded && (
                <>
                    <GoogleMap
                        mapContainerStyle={{ height: "100%", width: "100%" }}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        onZoomChanged={handleZoomChange}
                        onIdle={onIdle}
                        center={coordinates as Coordinates}
                        zoom={zoom}
                        options={{
                            mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAPID as string,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            zoomControl: false,
                            minZoom: MAP_MIN_ZOOM,
                            maxZoom: MAP_MAX_ZOOM,
                            streetViewControl: false,
                            gestureHandling: "greedy",
                        }}
                    >
                        <Markers
                            listings={listings}
                            lang={lang}
                            onListingClick={(l) => {
                                onListingClick(l);
                            }}
                            selectedRoom={selectedRoomMouseHover}
                            container={refContainer}
                        />
                    </GoogleMap>
                    <div className="absolute top-4 left-4 flex flex-col">
                        <Button
                            variant="white"
                            size="md"
                            fontSize="lg"
                            icon={faPlus}
                            className={`${buttonSize} rounded-none rounded-t-md`}
                            onClick={onZoomIn}
                        ></Button>
                        <Button
                            variant="white"
                            size="md"
                            fontSize="lg"
                            icon={faMinus}
                            className={`${buttonSize} rounded-none rounded-b-md border-t-0`}
                            onClick={onZoomOut}
                        ></Button>
                    </div>
                    {onCloseMap && (
                        <Button
                            isLoading={isMobile && isLoading}
                            variant="white"
                            size="md"
                            fontSize="lg"
                            icon={isMobile ? faList : faXmark}
                            className={`${isMobile ? "h-12 px-8" : buttonSize} absolute top-4 right-4 font-medium`}
                            onClick={onCloseMap}
                        >
                            {isMobile && "List"}
                        </Button>
                    )}
                    {!isMobile && (
                        <Button
                            variant="white"
                            size="md"
                            fontSize="lg"
                            icon={isFullscreen ? faCompressWide : faExpandWide}
                            className={`${buttonSize} absolute bottom-4 right-4`}
                            onClick={onFullscreen}
                        ></Button>
                    )}
                </>
            )}
        </div>
    );
};

export default SearchMap;
